import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getCodeData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { showDialog } from "@widgets/CommonDialog";
import { showPopupModal } from "@widgets/CommonModal";
import SN_PRD_BAS_REP_013_POP_01 from "./SN_PRD_BAS_REP_013_POP_01";

// 근무유형 코드클래스(mas_codel MAS067): 1 평일 · 2 반일 · 3 휴일.
const WORK_TYPE_CLASS = "MAS067";
// 색상은 코드클래스에 없어 유형(코드값)별로 지정. 평일=회색(주말이어도) / 반일=파랑 / 휴일=빨강.
const WORK_TYPE_COLOR: Record<string, string> = {
  "1": "text-gray-500",
  "2": "text-blue-600",
  "3": "text-red-600",
};

export type CalendarRow = {
  cd_firm: string;
  dt: string;
  tp_work: string;
  dc_holiday: string;
  cd_user_reg?: string;
  cd_user_amd?: string;
};

type PopupState = {
  from: string;
  to: string;
  tp_work: string;
  dc_holiday: string;
};

type Props = {
  queryId: string;
  saveEndPoint: string;
  ym: string;
  cdFirm?: string;
};

export type PrdCalendarRef = {
  onSearch: () => Promise<void>;
  onCreateCalendar: () => Promise<void>;
};

const pad = (value: number) => String(value).padStart(2, "0");
const toDateKey = (year: number, month: number, day: number) =>
  String(year) + pad(month) + pad(day);
const parseDate = (date: string) =>
  new Date(
    Number(date.slice(0, 4)),
    Number(date.slice(4, 6)) - 1,
    Number(date.slice(6, 8))
  );
const moveDateKey = (date: string, diff: number) => {
  const target = parseDate(date);
  target.setDate(target.getDate() + diff);
  return toDateKey(
    target.getFullYear(),
    target.getMonth() + 1,
    target.getDate()
  );
};
const getScheduleKey = (row?: CalendarRow) =>
  row ? row.tp_work + "|" + row.dc_holiday?.trim() : "";

const PrdCalendar = forwardRef<PrdCalendarRef, Props>(
  ({ queryId, saveEndPoint, ym, cdFirm }, ref) => {
    const userInfo: any = global.getState().userInfo;
    const today = new Date();
    const year = Number(ym?.slice(0, 4)) || today.getFullYear();
    const month = Number(ym?.slice(4, 6)) || today.getMonth() + 1;
    const [rows, setRows] = useState<CalendarRow[]>([]);
    const [dragStart, setDragStart] = useState("");
    const [dragEnd, setDragEnd] = useState("");
    const [isDragging, setIsDragging] = useState(false);
    // 근무유형(MAS067) 코드 목록 — 팝업 기본값 계산에만 사용.
    const [workTypes, setWorkTypes] = useState<{ code: string; name: string }[]>([]);

    useEffect(() => {
      (async () => {
        const list = await getCodeData(WORK_TYPE_CLASS, "");
        setWorkTypes(Array.isArray(list) ? list : []);
      })();
    }, []);

    // 기본 근무유형 = 첫 코드(평일). 못 불러오면 '1'(평일) 폴백.
    const defaultWorkType = workTypes[0]?.code ?? "1";

    const selectedDates = useMemo(() => {
      if (!dragStart || !dragEnd) return new Set<string>();
      const start = dragStart <= dragEnd ? dragStart : dragEnd;
      const end = dragStart <= dragEnd ? dragEnd : dragStart;
      const dates = new Set<string>();
      for (
        let d = parseDate(start);
        d <= parseDate(end);
        d.setDate(d.getDate() + 1)
      ) {
        dates.add(toDateKey(d.getFullYear(), d.getMonth() + 1, d.getDate()));
      }
      return dates;
    }, [dragStart, dragEnd]);

    const dataByDate = useMemo(() => {
      const map = new Map<string, CalendarRow[]>();
      rows.forEach((row) => map.set(row.dt, [...(map.get(row.dt) ?? []), row]));
      return map;
    }, [rows]);

    const calendarCells = useMemo(() => {
      const firstDay = new Date(year, month - 1, 1).getDay();
      const lastDate = new Date(year, month, 0).getDate();
      return Array.from({ length: 42 }, (_, index) => {
        const day = index - firstDay + 1;
        return day > 0 && day <= lastDate ? day : 0;
      });
    }, [year, month]);

    const onSearch = async () => {
      if (!queryId || !ym) return;
      const data: any = await fnGetDataSet({
        id: queryId,
        params: {
          cd_firm: cdFirm ?? userInfo.cd_firm,
          ym,
        },
      });
      setRows(data?.[0] ?? []);
    };

    useEffect(() => {
      onSearch();
    }, [ym]);

    const onCreateCalendar = async () => {
      const baseYm = String(ym ?? "")
        .replace(/[^0-9]/g, "")
        .slice(0, 6);
      if (baseYm.length !== 6) return;

      if (dataByDate.size > 0) {
        const confirm = await showDialog(
          "confirm",
          "이미 생성된 데이터가 있습니다, 그래도 생성하시겠습니까?"
        );
        if (!confirm) return;
      }

      const ok = await fnSave(
        "SN_PRD_BAS_REP_013_CREATE_CALENDAR",
        {
          cd_firm: cdFirm ?? userInfo.cd_firm,
          ym: baseYm,
        },
        undefined,
        { silentSuccess: true }
      );
      if (ok) await onSearch();
    };

    useImperativeHandle(ref, () => ({ onSearch, onCreateCalendar }));

    const openPopup = async (date: string) => {
      if (dataByDate.size === 0) {
        await showDialog(
          "warning",
          "선택하신 기준년월의 달력이 생성되지 않았습니다.\n" +
            "달력 생성 후 다시 시도해주시기 바랍니다."
        );
        return;
      }

      const dates = selectedDates.has(date)
        ? [...selectedDates].sort()
        : [date];
      const row =
        (dataByDate.get(date) ?? []).find((item) => item.dc_holiday?.trim()) ??
        dataByDate.get(date)?.[0];
      const selectedData: PopupState = {
        from: dates[0],
        to: dates[dates.length - 1],
        tp_work: row?.tp_work || defaultWorkType,
        dc_holiday: row?.dc_holiday || "",
      };
      const result = await showPopupModal({
        title: "일정등록",
        children: <SN_PRD_BAS_REP_013_POP_01 selectedData={selectedData} />,
        width: "620px",
        height: "320px",
      });

      if (!result || result === "close") return;
      await savePopup(result);
    };

    const savePopup = async (popupData: PopupState) => {
      if (!popupData) return;

      const updateRows: CalendarRow[] = [];
      for (
        let d = parseDate(popupData.from);
        d <= parseDate(popupData.to);
        d.setDate(d.getDate() + 1)
      ) {
        updateRows.push({
          cd_firm: cdFirm ?? userInfo.cd_firm,
          dt: toDateKey(d.getFullYear(), d.getMonth() + 1, d.getDate()),
          tp_work: popupData.tp_work,
          dc_holiday: String(popupData.dc_holiday ?? "").trim(),
        });
      }

      const ok = await fnSave(
        saveEndPoint,
        { mas_calendar: { update: updateRows } },
        undefined,
        { silentSuccess: true }
      );
      if (ok) {
        await showDialog("success", "저장되었습니다.");
        await onSearch();
      }
    };

    return (
      <>
        <div
          className="flex-1 p-4 overflow-auto"
          onMouseUp={() => setIsDragging(false)}
        >
          <div className="grid grid-cols-7 border-l border-t border-gray-300 bg-white min-w-[900px]">
            {["SUN", "MON", "TUE", "WED", "THR", "FRI", "SAT"].map(
              (day, index) => {
                const headClass =
                  index === 0
                    ? "bg-[#10106f]"
                    : index === 6
                      ? "bg-[#2f37c9]"
                      : "bg-[#d9dee4]";
                return (
                  <div
                    key={day}
                    className={
                      "h-8 border-r border-b border-gray-300 text-center leading-8 text-xs font-bold text-white " +
                      headClass
                    }
                  >
                    {day}
                  </div>
                );
              }
            )}
            {calendarCells.map((day, index) => {
              const date = day ? toDateKey(year, month, day) : "";
              const dayRows = date ? (dataByDate.get(date) ?? []) : [];
              const items = dayRows.filter((item) => item.dc_holiday?.trim());
              const workType = dayRows.find((item) => item.tp_work)?.tp_work;
              const selectedClass =
                date && selectedDates.has(date) ? "bg-[#b8dcf5]" : "bg-white";
              const dayClass = workType
                ? (WORK_TYPE_COLOR[workType] ?? "text-gray-500")
                : index % 7 === 0
                  ? "text-red-600"
                  : index % 7 === 6
                    ? "text-blue-600"
                    : "text-gray-500";
              return (
                <div
                  key={index}
                  className={
                    "min-h-[115px] border-r border-b border-gray-200 p-2 select-none " +
                    selectedClass
                  }
                  onMouseDown={(e) => {
                    if (!date || e.detail > 1 || e.button !== 0) return;
                    setIsDragging(true);
                    setDragStart(date);
                    setDragEnd(date);
                  }}
                  onMouseEnter={() => date && isDragging && setDragEnd(date)}
                  onContextMenu={(e) => {
                    if (!date) return;
                    e.preventDefault();
                    openPopup(date);
                  }}
                  onDoubleClick={() => date && openPopup(date)}
                >
                  {date && (
                    <div className={dayClass + " text-xs mb-1"}>{day}</div>
                  )}
                  {items.map((item) => {
                    const itemKey = getScheduleKey(item);
                    const prevSame =
                      date &&
                      index % 7 !== 0 &&
                      (dataByDate.get(moveDateKey(date, -1)) ?? []).some(
                        (row) => getScheduleKey(row) === itemKey
                      );
                    const nextSame =
                      date &&
                      index % 7 !== 6 &&
                      (dataByDate.get(moveDateKey(date, 1)) ?? []).some(
                        (row) => getScheduleKey(row) === itemKey
                      );
                    const mergeClass =
                      (prevSame
                        ? " -ml-2 rounded-l-none border-l-0 pl-3"
                        : "") +
                      (nextSame ? " -mr-2 rounded-r-none border-r-0" : "");

                    return (
                      <div
                        key={item.dt + itemKey}
                        className={
                          "truncate rounded px-1 py-[2px] mb-1 bg-[#b8dcf5] text-[#10106f] border border-[#aab7c4]" +
                          mergeClass
                        }
                        title={item.dc_holiday}
                      >
                        {prevSame ? "\u00a0" : item.dc_holiday}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  }
);

export default PrdCalendar;
